import React from 'react'
import { motion } from 'framer-motion'
import { getFunnyTitle } from '../../../../utils/funnyTitles'

// Define interfaces for the component props
interface Competitor {
  id: string;
  name: string;
  image: string;
  points: number;
  bugs: number;
  role?: string;
  coins?: number;
  /* Index signature added so this interface is compatible with other
     Competitor variants that include additional dynamic properties
     (avoids “index signature” TypeScript errors in consumer code). */
  [key: string]: unknown;
}

interface IntroducedCompetitorProps {
  competitor: Competitor;
  index: number;
  animationState: 'entering' | 'visible' | 'exiting';
  borderColor?: string;
  useFunnyTitles?: boolean;
}

const IntroducedCompetitor: React.FC<IntroducedCompetitorProps> = ({
  competitor,
  index,
  animationState, 
  borderColor = '#8a2be2',
  useFunnyTitles = true
}) => {
  // Calculate delays based on index
  const baseDelay = index * 0.2 // 300ms stagger between competitors

  // Get the appropriate title based on setting
  const title = getFunnyTitle(competitor, useFunnyTitles)

  return (
    <motion.div
      className="flex flex-col items-center justify-center w-[200px] mb-8 mx-2"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{
        opacity: animationState === 'exiting' ? 0 : 1,
        scale: animationState === 'exiting' ? 0.5 : 1
      }}
      transition={{
        duration: 0.3,
        delay: animationState === 'entering' ? baseDelay :
          animationState === 'exiting' ? baseDelay : 0
      }}
    >
      <img
        src={competitor.image}
        alt={competitor.name}
        className="w-32 h-32 object-cover rounded-full"
        style={{
          borderWidth: '4px',
          borderStyle: 'solid',
          borderColor,
          boxShadow: '2px 2px 1px rgba(0, 0, 0, 0.2)'
        }}
      />

      <motion.div
        className="mt-3 text-xl font-bold text-center w-full caprasimo-regular race-title-color"
        initial={{ opacity: 0 }}
        animate={{ opacity: animationState === 'exiting' ? 0 : 1 }}
        transition={{
          duration: 0.2,
          delay: animationState === 'entering' ? baseDelay + 0.2 :
            animationState === 'exiting' ? baseDelay : 0
        }}
        style={{ textShadow: '2px 2px 1px rgba(0, 0, 0, 0.2)' }}
      >
        {competitor.name}
      </motion.div>

      <motion.div
        className="text-lg text-gray-900 caprasimo-regular"
        initial={{ opacity: 0 }}
        animate={{ opacity: animationState === 'exiting' ? 0 : 1 }}
        transition={{
          duration: 0.2,
          delay: animationState === 'entering' ? baseDelay + 0.3 :
            animationState === 'exiting' ? baseDelay + 0.1 : 0
        }}
        style={{ textShadow: '2px 2px 1px rgba(0, 0, 0, 0.2)' }}
      >
        &quot;{title}&quot;
      </motion.div>
    </motion.div>
  )
}

export default IntroducedCompetitor
